import { useNavigate } from "react-router-dom";
import PlusIcon from "@assets/svg/PlusIcon";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { useForm } from "react-hook-form";
import { colors } from "@utils/theme";
import { useSnackbar } from "notistack";
// @ts-ignore
import FileIcon from "@assets/images/fileIcon.png";
import { useEffect, useRef, useState } from "react";
import { getReferralContacts } from "@endpoints/user.service";
import { isNullOrEmpty, timeAgo } from "@utils/helperFunction";
import dayjs from "@lib/dayjs";

const CandidateRow = ({ member, navigate, index }) => {
  return (
    <>
      <div
        className={`grid grid-cols-[0.2fr_0.4fr_1fr_0.3fr] items-center gap-8 px-4 py-5 text-xs  ${
          index !== 0 ? "border-t" : ""
        } text-gray-700 radius-[4px] border-gray-200 hover:bg-gray-50 cursor-pointer`}
        // onClick={() => navigate("/network-detail", { state: member })}
        onClick={() =>
          navigate(`/network/${member?.candidateId}`, {
            state: member,
          })
        }
      >
        <div className="flex flex-col items-center gap-0.5 w-max">
          {member?.isActive ? (
            <div className="flex items-center gap-1 text-xss italic">
              <div className="h-2 w-2 rounded-full bg-green-600" />
              Online
            </div>
          ) : (
            <span className="text-xss italic">
              {timeAgo(member?.inactiveMinutes)}
            </span>
          )}
          <div className="h-9 w-9 rounded-full  flex gap-4 items-center font-bold text-gray-800 bg-[#E1E0E0] bg-nwhited">
            {member?.profilePic ? (
              <img
                src={member?.profilePic}
                className="h-full w-full object-cover rounded-full"
                alt="profileImg"
              />
            ) : (
              <span className="w-12 font-montserrat text-white text-center items-center font-semibold text-sm">
                {member?.firstName?.charAt(0).toUpperCase()}
                {member?.lastName?.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <span className="text-center text-xss text-light-black w-max">
            {member?.status
              ?.toLowerCase()
              .replace(/_/g, " ")
              .replace(/^./, (c) => c.toUpperCase()) ?? "N/A"}
          </span>
        </div>
        <div className="flex flex-col gap-4">
          <span className="text-center text-xss text-light-black w-max">
            {member?.refNo}
          </span>
          <div className="flex flex-col gap-1.5">
            <span className="font-bold capitalize text-primary items-center text-xs">
              {member?.firstName} {member?.lastName}
            </span>
            <span className="font-medium text-xs text-primary items-center">
              {isNullOrEmpty(member?.jobTitle) ? "N/A" : member?.jobTitle}{" "}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xss text-light-black">
            {dayjs().format("hh:mma  DD MMM YY")}
          </span>
          <div className="flex flex-wrap items-center text-xs font-medium text-primary bg-light-white-700 rounded-sm px-4 h-[52px] leading-5">
            <span className="font-bold">
              Joe Bloggs
              <span className="font-medium">
                &nbsp; accepted your invitation and is now part of your network
              </span>
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center text-center gap-3">
          <span className="text-primary underline text-xss">Matched</span>
          <span className="font-medium text-primary text-xs">
            {member?.totalJobMatches}{" "}
          </span>
        </div>
      </div>
    </>
  );
};

const MyNetwork = () => {
  const observerRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    control,
    formState: { errors },
    watch,
    setValue,
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      inviteModal: false,
      isSubmitting: false,
      firstName: undefined,
      lastName: undefined,
      email: undefined,
    },
  });
  const fields = watch();

  // Local state
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      try {
        const response = await getReferralContacts({ page, limit });
        const { data = [], success } = response;

        if (success) {
          setMembers((prev) => {
            const ids = new Set(prev.map((item) => item._id));
            const newItems = data.filter((item) => !ids.has(item._id));
            return [...prev, ...newItems];
          });
          setHasMore(data.length === limit);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        enqueueSnackbar("Failed to load contacts", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    if (hasMore) {
      fetchMembers();
    }
  }, [page]);

  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );

    const currentElement = observerRef.current;
    if (currentElement) observer.observe(currentElement);

    return () => {
      if (currentElement) observer.unobserve(currentElement);
    };
  }, [loading, hasMore]);

  useEffect(() => {
    // setMembers([]);
    setPage(1);
  }, []);

  return loading ? (
    <div className="gap-2.5 flex flex-col w-full">
      {Array.from({ length: 10 }, (x, i) => i).map((key) => {
        return (
          <div className="flex justify-between">
            <div
              style={{
                borderWidth: 1,
              }}
              className="flex rounded-lg w-full pl-3 pr-8 py-4 gap-1.5"
            >
              <div className="h-3 rounded bg-gray-200 w-full"></div>
              <div className="h-3 rounded bg-gray-200 w-full"></div>
              <div className="h-3 rounded bg-gray-200 w-full"></div>
            </div>
          </div>
        );
      })}
    </div>
  ) : members && members.length > 0 ? (
    <div className="w-full min-h-0 flex flex-col flex-1">
      {/* Search section */}
      <div className="flex items-center gap-2 mb-5">
        <div className="bg-light-white-700 rounded-md p-1.5 w-72">
          <input
            className="bg-white rounded-md text-xs h-7 px-3 font-medium w-full outline-none"
            placeholder="Search"
          />
        </div>
        <button className="bg-light-white-700 h-10 px-12 text-xs rounded-md flex items-center justify-center font-bold text-primary hover:bg-gray-100">
          Filter
        </button>
        <button className="bg-light-white-700 h-10 px-12 text-xs rounded-md flex items-center justify-center font-bold text-primary hover:bg-gray-100">
          Sort By
        </button>
      </div>
      <div className="w-full rounded-md overflow-y-scroll border-t no-scrollbar min-h-0">
        {members?.map((member, index) => {
          return (
            <CandidateRow
              key={index}
              index={index}
              member={member}
              navigate={navigate}
            />
          );
        })}
        <div ref={observerRef} />
        {loading && (
          <div className="flex justify-center py-4">
            <LoadingAnimation color={colors.PrimaryBlack} className="w-5 h-5" />
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className="flex flex-col h-full justify-center items-center">
      <img src={FileIcon} alt="File Icon" />
      <p className="font-semibold text-sm text-primary">
        You currently don’t have any contact.
      </p>
      <div className="flex flex-col gap-2 mt-3">
        <p className="font-medium text-xs text-light-black">
          Click here to invite.
        </p>
        <button
          onClick={() => setValue("inviteModal", true)}
          className="flex items-center justify-center border bg-green-600 rounded-lg px-2 py-1 gap-1 text-white font-semibold text-sm"
        >
          <PlusIcon className={"w-4 h-4"} fill={"white"} />
          Invite
        </button>
      </div>
    </div>
  );
};

export default MyNetwork;
